var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2023 Félix Robles <felix@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { Box, Button, CircularProgress, Typography, Alert } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, IconButton, PageLimit, SelectElection, theme } from "@sequentech/ui-essentials";
import { stringToHtml, translateFromPresentation, EVotingStatus, isUndefined, } from "@sequentech/ui-core";
import { AuthContext } from "../providers/AuthContextProvider";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { styled } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectBallotStyleByElectionId, selectBallotStyleElectionIds, selectFirstBallotStyle, setBallotStyle, } from "../store/ballotStyles/ballotStylesSlice";
import { resetBallotSelection } from "../store/ballotSelections/ballotSelectionsSlice";
import { selectElectionById, setElection, selectElectionIds } from "../store/elections/electionsSlice";
import { addCastVotes, selectCastVotesByElectionId } from "../store/castVotes/castVotesSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_BALLOT_STYLES } from "../queries/GetBallotStyles";
import { GET_ELECTIONS } from "../queries/GetElections";
import { ELECTIONS_LIST } from "../fixtures/election";
import { SettingsContext } from "../providers/SettingsContextProvider";
import { GET_ELECTION_EVENT } from "../queries/GetElectionEvent";
import { GET_CAST_VOTES } from "../queries/GetCastVotes";
import { ElectionScreenErrorType, ElectionScreenMsgType, VotingPortalError, VotingPortalErrorType, } from "../services/VotingPortalError";
import { selectElectionEventById, setElectionEvent, } from "../store/electionEvents/electionEventsSlice";
import Stepper from "../components/Stepper";
import { clearIsVoted, selectBypassChooser, setBypassChooser } from "../store/extra/extraSlice";
import { updateBallotStyleAndSelection } from "../services/BallotStyles";
import useUpdateTranslation from "../hooks/useUpdateTranslation";
import { GET_SUPPORT_MATERIALS } from "../queries/GetSupportMaterials";
import { setSupportMaterial } from "../store/supportMaterials/supportMaterialsSlice";
var StyledTitle = styled(Typography)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    margin-top: 25.5px;\n    display: flex;\n    flex-direction: row;\n    gap: 16px;\n    font-size: 24px;\n    font-weight: 500;\n    line-height: 27px;\n    margin-top: 20px;\n    margin-bottom: 16px;\n"], ["\n    margin-top: 25.5px;\n    display: flex;\n    flex-direction: row;\n    gap: 16px;\n    font-size: 24px;\n    font-weight: 500;\n    line-height: 27px;\n    margin-top: 20px;\n    margin-bottom: 16px;\n"])));
var ElectionContainer = styled(Box)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    gap: 30px;\n    margin-bottom: 30px;\n"], ["\n    display: flex;\n    flex-direction: column;\n    gap: 30px;\n    margin-bottom: 30px;\n"])));
var isElectionEventOpen = function (electionEvent) {
    var _a, _b;
    return (((_b = (_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.status) === null || _a === void 0 ? void 0 : _a.voting_status) !== null && _b !== void 0 ? _b : EVotingStatus.CLOSED) === EVotingStatus.OPEN);
};
var ElectionWrapper = function (_a) {
    var _b;
    var electionId = _a.electionId, bypassChooser = _a.bypassChooser, canVoteTest = _a.canVoteTest;
    var navigate = useNavigate();
    var location = useLocation();
    var i18n = useTranslation().i18n;
    var _c = useParams(), tenantId = _c.tenantId, eventId = _c.eventId;
    var electionEvent = useAppSelector(selectElectionEventById(eventId));
    var election = useAppSelector(selectElectionById(electionId));
    var ballotStyle = useAppSelector(selectBallotStyleByElectionId(electionId));
    var castVotes = useAppSelector(selectCastVotesByElectionId(String(electionId)));
    var _d = useState(false), visitedBypassChooser = _d[0], setVisitedBypassChooser = _d[1];
    var authContext = useContext(AuthContext);
    var isKiosk = authContext.isKiosk();
    if (!election) {
        throw new VotingPortalError(VotingPortalErrorType.INTERNAL_ERROR);
    }
    var electionStatus = election === null || election === void 0 ? void 0 : election.status;
    var isVotingOpen = ((electionStatus === null || electionStatus === void 0 ? void 0 : electionStatus.voting_status) === EVotingStatus.OPEN ||
        (isKiosk && (electionStatus === null || electionStatus === void 0 ? void 0 : electionStatus.kiosk_voting_status) === EVotingStatus.OPEN)) &&
        isElectionEventOpen(electionEvent);
    var isPreview = sessionStorage.getItem("isDemo") === "true";
    var canVote = function () {
        var _a, _b;
        if (!canVoteTest && !((_a = election.name) === null || _a === void 0 ? void 0 : _a.includes("TEST"))) {
            return false;
        }
        if ((ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.num_allowed_revotes) === 0) {
            return true;
        }
        return (isPreview ||
            (castVotes.length < ((_b = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.num_allowed_revotes) !== null && _b !== void 0 ? _b : 1) && isVotingOpen));
    };
    var onClickToVote = function () {
        console.log("onClickToVote");
        if (!canVote() || (!isPreview && !isElectionEventOpen(electionEvent))) {
            console.log("cannot vote");
            return;
        }
        navigate("/tenant/".concat(tenantId, "/event/").concat(eventId, "/election/").concat(electionId, "/start").concat(location.search));
    };
    var handleClickBallotLocator = function () {
        navigate("../election/".concat(electionId, "/ballot-locator").concat(location.search));
    };
    useEffect(function () {
        if (visitedBypassChooser) {
            console.log("visitedBypassChooser");
            return;
        }
        if (bypassChooser && ballotStyle) {
            console.log("setVisitedBypassChooser");
            setVisitedBypassChooser(true);
            onClickToVote();
        }
    }, [bypassChooser, visitedBypassChooser, setVisitedBypassChooser, ballotStyle]);
    return (_jsx(SelectElection, { isActive: canVote(), isOpen: isVotingOpen, title: translateFromPresentation(election, "name", i18n.language) || "-", hasVoted: castVotes.length > 0, onClickToVote: canVote() ? onClickToVote : undefined, onClickBallotLocator: handleClickBallotLocator, electionDates: (_b = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml) === null || _b === void 0 ? void 0 : _b.election_dates }));
};
var fakeUpdateBallotStyleAndSelection = function (dispatch) {
    for (var _i = 0, ELECTIONS_LIST_1 = ELECTIONS_LIST; _i < ELECTIONS_LIST_1.length; _i++) {
        var election = ELECTIONS_LIST_1[_i];
        try {
            var formattedBallotStyle = {
                id: election.id,
                election_id: election.id,
                election_event_id: election.id,
                tenant_id: election.id,
                ballot_eml: election,
                ballot_signature: null,
                created_at: "",
                area_id: election.id,
                annotations: null,
                labels: null,
                last_updated_at: "",
            };
            dispatch(setElection(__assign(__assign({}, election), { image_document_id: "" })));
            dispatch(setBallotStyle(formattedBallotStyle));
            dispatch(clearIsVoted());
            dispatch(resetBallotSelection({
                ballotStyle: formattedBallotStyle,
            }));
        }
        catch (error) {
            console.log("Error loading fake EML: ".concat(error), election);
            throw new VotingPortalError(VotingPortalErrorType.INTERNAL_ERROR);
        }
    }
};
var ElectionSelectionScreen = function () {
    var _a, _b;
    var t = useTranslation().t;
    var navigate = useNavigate();
    var location = useLocation();
    var _c = useContext(SettingsContext), globalSettings = _c.globalSettings, defaultLanguageTouched = _c.defaultLanguageTouched, setDefaultLanguageTouched = _c.setDefaultLanguageTouched;
    var _d = useParams(), eventId = _d.eventId, tenantId = _d.tenantId;
    var electionEvent = useAppSelector(selectElectionEventById(eventId));
    var oneBallotStyle = useAppSelector(selectFirstBallotStyle);
    useUpdateTranslation({ electionEvent: electionEvent }, defaultLanguageTouched, setDefaultLanguageTouched); // Overwrite translations
    var ballotStyleElectionIds = useAppSelector(selectBallotStyleElectionIds);
    var electionIds = useAppSelector(selectElectionIds);
    var dispatch = useAppDispatch();
    var _e = useState(true), canVoteTest = _e[0], setCanVoteTest = _e[1];
    var _f = useState(null), testElectionId = _f[0], setTestElectionId = _f[1];
    var castVotesTestElection = useAppSelector(selectCastVotesByElectionId(String(testElectionId || tenantId)));
    var _g = useState(false), openChooserHelp = _g[0], setOpenChooserHelp = _g[1];
    var _h = useState(false), isMaterialsActivated = _h[0], setIsMaterialsActivated = _h[1];
    var bypassChooser = useAppSelector(selectBypassChooser());
    var _j = useState(), errorMsg = _j[0], setErrorMsg = _j[1];
    var _k = useState(), alertMsg = _k[0], setAlertMsg = _k[1];
    var _l = useQuery(GET_BALLOT_STYLES, {
        skip: globalSettings.DISABLE_AUTH, // Skip query if in demo mode
    }), errorBallotStyles = _l.error, dataBallotStyles = _l.data, loadingBallotStyles = _l.loading;
    var _m = useQuery(GET_ELECTIONS, {
        variables: {
            electionIds: ballotStyleElectionIds,
        },
        skip: globalSettings.DISABLE_AUTH, // Skip query if in demo mode
    }), errorElections = _m.error, dataElections = _m.data, loadingElections = _m.loading;
    var _o = useQuery(GET_ELECTION_EVENT, {
        variables: {
            electionEventId: eventId,
            tenantId: tenantId,
        },
        skip: globalSettings.DISABLE_AUTH, // Skip query if in demo mode
    }), errorElectionEvent = _o.error, dataElectionEvent = _o.data, loadingElectionEvent = _o.loading;
    // Materials
    var _p = useQuery(GET_SUPPORT_MATERIALS, {
        variables: {
            electionEventId: eventId || "",
            tenantId: tenantId || "",
        },
        skip: globalSettings.DISABLE_AUTH || !isMaterialsActivated, // Skip query if in demo mode
    }), dataMaterials = _p.data, errorMaterials = _p.error, loadingMaterials = _p.loading;
    var _q = useQuery(GET_CAST_VOTES, {
        skip: globalSettings.DISABLE_AUTH,
    }), castVotes = _q.data, errorCastVote = _q.error;
    var handleNavigateMaterials = function () {
        navigate("/tenant/".concat(tenantId, "/event/").concat(eventId, "/materials").concat(location.search));
    };
    var hasNoElections = !loadingElections && (dataElections === null || dataElections === void 0 ? void 0 : dataElections.sequent_backend_election.length) === 0;
    var isPublished = useMemo(function () { var _a; return !!((_a = dataElectionEvent === null || dataElectionEvent === void 0 ? void 0 : dataElectionEvent.sequent_backend_election_event[0].status) === null || _a === void 0 ? void 0 : _a.is_published); }, [dataElectionEvent === null || dataElectionEvent === void 0 ? void 0 : dataElectionEvent.sequent_backend_election_event]);
    useEffect(function () {
        if (!dataMaterials || globalSettings.DISABLE_AUTH || !isMaterialsActivated) {
            return;
        }
        for (var _i = 0, _a = dataMaterials.sequent_backend_support_material; _i < _a.length; _i++) {
            var material = _a[_i];
            dispatch(setSupportMaterial(material));
        }
    }, [dataMaterials, globalSettings.DISABLE_AUTH, isMaterialsActivated]);
    // Errors handling
    useEffect(function () {
        if (globalSettings.DISABLE_AUTH) {
            return;
        }
        if (errorElections || errorElectionEvent || errorBallotStyles || errorCastVote) {
            if (errorBallotStyles === null || errorBallotStyles === void 0 ? void 0 : errorBallotStyles.message.includes("x-hasura-area-id")) {
                setErrorMsg(t("electionSelectionScreen.errors.".concat(ElectionScreenErrorType.NO_AREA)));
            }
            else if ((errorElections === null || errorElections === void 0 ? void 0 : errorElections.networkError) ||
                (errorElectionEvent === null || errorElectionEvent === void 0 ? void 0 : errorElectionEvent.networkError) ||
                (errorBallotStyles === null || errorBallotStyles === void 0 ? void 0 : errorBallotStyles.networkError) ||
                (errorCastVote === null || errorCastVote === void 0 ? void 0 : errorCastVote.networkError)) {
                setErrorMsg(t("electionSelectionScreen.errors.".concat(ElectionScreenErrorType.NETWORK)));
            }
            else {
                setErrorMsg(t("electionSelectionScreen.errors.".concat(ElectionScreenErrorType.FETCH_DATA)));
            }
        }
        else if ((dataElectionEvent === null || dataElectionEvent === void 0 ? void 0 : dataElectionEvent.sequent_backend_election_event.length) === 0) {
            setErrorMsg(t("electionSelectionScreen.errors.".concat(ElectionScreenErrorType.NO_ELECTION_EVENT)));
        }
        else if (!isPublished) {
            setAlertMsg(t("electionSelectionScreen.alerts.".concat(ElectionScreenMsgType.NOT_PUBLISHED)));
        }
        else if (hasNoElections) {
            if (electionIds.length > 0) {
                setErrorMsg(t("electionSelectionScreen.errors.".concat(ElectionScreenErrorType.OBTAINING_ELECTION), { electionIds: JSON.stringify(electionIds) }));
            }
            else {
                setAlertMsg(t("electionSelectionScreen.alerts.".concat(ElectionScreenMsgType.NO_ELECTIONS)));
            }
        }
        else {
            setAlertMsg(undefined);
            setErrorMsg(undefined);
        }
    }, [
        errorBallotStyles,
        errorCastVote,
        errorElectionEvent,
        errorElections,
        isPublished,
        hasNoElections,
        dataElectionEvent,
        globalSettings.DISABLE_AUTH,
    ]);
    useEffect(function () {
        if (dataBallotStyles && dataBallotStyles.sequent_backend_ballot_style.length > 0) {
            try {
                updateBallotStyleAndSelection(dataBallotStyles, dispatch);
            }
            catch (_a) {
                setErrorMsg(t("electionSelectionScreen.errors.".concat(ElectionScreenErrorType.BALLOT_STYLES_EML)));
            }
        }
        else if (globalSettings.DISABLE_AUTH) {
            //fakeUpdateBallotStyleAndSelection(dispatch)
        }
    }, [globalSettings.DISABLE_AUTH, dataBallotStyles, dispatch]);
    useEffect(function () {
        var _a, _b;
        if (dataElections && dataElections.sequent_backend_election.length > 0) {
            for (var _i = 0, _c = dataElections.sequent_backend_election; _i < _c.length; _i++) {
                var election = _c[_i];
                dispatch(setElection(__assign(__assign({}, election), { image_document_id: "", contests: [], description: (_a = election.description) !== null && _a !== void 0 ? _a : undefined, alias: (_b = election.alias) !== null && _b !== void 0 ? _b : undefined })));
            }
            var foundTestElection = dataElections.sequent_backend_election.find(function (election) {
                return election.name.includes("TEST");
            });
            if (foundTestElection) {
                setCanVoteTest(false);
            }
            setTestElectionId((foundTestElection === null || foundTestElection === void 0 ? void 0 : foundTestElection.id) || null);
        }
    }, [dataElections, dispatch]);
    useEffect(function () {
        if (!testElectionId) {
            return;
        }
        setCanVoteTest(castVotesTestElection.length > 0);
    }, [castVotesTestElection, testElectionId, setCanVoteTest]);
    useEffect(function () {
        var _a;
        var record = (_a = dataElectionEvent === null || dataElectionEvent === void 0 ? void 0 : dataElectionEvent.sequent_backend_election_event) === null || _a === void 0 ? void 0 : _a[0];
        if (record) {
            dispatch(setElectionEvent(record));
        }
    }, [dataElectionEvent, dispatch]);
    useEffect(function () {
        var _a, _b;
        setIsMaterialsActivated(((_b = (_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) === null || _a === void 0 ? void 0 : _a.materials) === null || _b === void 0 ? void 0 : _b.activated) || false);
    }, [(_b = (_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) === null || _a === void 0 ? void 0 : _a.materials) === null || _b === void 0 ? void 0 : _b.activated]);
    useEffect(function () {
        if (castVotes === null || castVotes === void 0 ? void 0 : castVotes.sequent_backend_cast_vote) {
            dispatch(addCastVotes(castVotes.sequent_backend_cast_vote));
        }
    }, [castVotes, dispatch]);
    useEffect(function () {
        var _a, _b;
        var skipPolicy = (_b = (_a = oneBallotStyle === null || oneBallotStyle === void 0 ? void 0 : oneBallotStyle.ballot_eml.election_event_presentation) === null || _a === void 0 ? void 0 : _a.skip_election_list) !== null && _b !== void 0 ? _b : false;
        console.log("skipPolicy", skipPolicy);
        var newBypassChooser = skipPolicy &&
            1 === electionIds.length &&
            !errorCastVote &&
            !isUndefined(castVotes) &&
            !!electionEvent &&
            !!dataElections;
        if (newBypassChooser && !bypassChooser) {
            console.log("new baypass chooser", newBypassChooser);
            dispatch(setBypassChooser(newBypassChooser));
        }
    }, [
        castVotes,
        electionIds,
        errorCastVote,
        castVotes,
        electionEvent,
        dataElections,
        oneBallotStyle,
    ]);
    if (loadingElectionEvent || loadingElections || loadingBallotStyles)
        return _jsx(CircularProgress, {});
    return (_jsxs(PageLimit, __assign({ maxWidth: "lg", className: "election-selection-screen screen" }, { children: [_jsx(Box, __assign({ marginTop: "48px" }, { children: _jsx(Stepper, { selected: 0 }) })), _jsxs(Box, __assign({ sx: {
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    minHeight: "100px",
                } }, { children: [_jsxs(Box, __assign({ sx: { width: "100%" } }, { children: [_jsxs(StyledTitle, __assign({ variant: "h1" }, { children: [_jsx(Box, { children: t("electionSelectionScreen.title") }), _jsx(IconButton, { icon: faCircleQuestion, sx: { fontSize: "unset", lineHeight: "unset", paddingBottom: "2px" }, fontSize: "16px", onClick: function () { return setOpenChooserHelp(true); } }), _jsx(Dialog, __assign({ handleClose: function () { return setOpenChooserHelp(false); }, open: openChooserHelp, title: t("electionSelectionScreen.chooserHelpDialog.title"), ok: t("electionSelectionScreen.chooserHelpDialog.ok"), variant: "info" }, { children: stringToHtml(t("electionSelectionScreen.chooserHelpDialog.content")) }))] })), errorMsg || alertMsg ? (_jsx(Alert, __assign({ severity: "warning" }, { children: errorMsg || alertMsg }))) : (_jsx(Typography, __assign({ variant: "body1", sx: { color: theme.palette.customGrey.contrastText } }, { children: stringToHtml(t("electionSelectionScreen.description")) })))] })), isMaterialsActivated ? (_jsx(Button, __assign({ onClick: handleNavigateMaterials }, { children: t("materials.common.label") }))) : null] })), _jsx(ElectionContainer, __assign({ className: "elections-list" }, { children: !hasNoElections ? (electionIds.map(function (electionId) { return (_jsx(ElectionWrapper, { electionId: electionId, bypassChooser: bypassChooser, canVoteTest: canVoteTest }, electionId)); })) : (_jsx(Box, __assign({ sx: { margin: "auto" } }, { children: _jsx(Typography, { children: t("electionSelectionScreen.noResults") }) }))) }))] })));
};
export default ElectionSelectionScreen;
var templateObject_1, templateObject_2;
